import { createContext, useContext, ReactNode, useState, useEffect } from 'react';

// Static Web Apps user info from /.auth/me
interface SWAUserInfo {
  clientPrincipal: {
    userId: string;
    userRoles: string[];
    claims: { typ: string; val: string }[];
    identityProvider: string;
    userDetails: string;
  } | null;
}

// Simplified user type for the app
interface AppUser {
  userId: string;
  name: string;
  username: string;
  roles: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AppUser | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AppUser | null>(null);

  useEffect(() => {
    // Fetch user info from Static Web Apps auth endpoint
    async function fetchUserInfo() {
      try {
        const response = await fetch('/.auth/me');
        const data: SWAUserInfo = await response.json();

        if (data.clientPrincipal) {
          const principal = data.clientPrincipal;

          // Extract name from claims
          const nameClaim = principal.claims.find(c => c.typ === 'name');
          const emailClaim = principal.claims.find(c =>
            c.typ === 'preferred_username' ||
            c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
          );

          setUser({
            userId: principal.userId,
            name: nameClaim?.val || principal.userDetails || 'User',
            username: emailClaim?.val || principal.userDetails || '',
            roles: principal.userRoles,
          });
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchUserInfo();
  }, []);

  const isAuthenticated = user !== null;

  const login = () => {
    // Redirect to Azure AD login via Static Web Apps
    // Preserve the full URL including path and query params for deep linking
    const fullPath = window.location.pathname + window.location.search;
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(fullPath);
  };

  const logout = () => {
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
